import { useEffect } from 'react'

type MakeCustomEventBindingsResult<T> = T extends undefined
  ? {
      (): void
      useListen(callback: () => void): void
      listen(callback: () => void): () => void
    }
  : {
      (detail: T): void
      useListen(callback: (detail: T) => void): void
      listen(callback: (detail: T) => void): () => void
    }

export function makeCustomEventBindings<T = undefined>(
  eventName: string,
): MakeCustomEventBindingsResult<T> {
  function emit(...args: T extends undefined ? [] : [T]) {
    const detail = args[0]
    document.dispatchEvent(new CustomEvent<T>(eventName, { detail }))
  }

  function useListen(callback: (detail: T) => void) {
    // event subscription
    useEffect(() => {
      const handler = (e: Event) => {
        callback((e as CustomEvent<T>).detail)
      }

      document.addEventListener(eventName, handler)
      return () => {
        document.removeEventListener(eventName, handler)
      }
    }, [callback, eventName])
  }

  function listen(callback: (detail: T) => void) {
    if (typeof document === 'undefined') return () => {} // for tests
    const handler = (e: Event) => {
      callback((e as CustomEvent<T>).detail)
    }
    document.addEventListener(eventName, handler)

    return () => {
      document.removeEventListener(eventName, handler)
    }
  }

  return Object.assign(emit, { useListen, listen }) as MakeCustomEventBindingsResult<T>
}
